import {
  isOrgSubdomainHostEnabled,
  normalizeSsoOrgLabel,
  resolveOrgKeyFromHostname,
} from "./orgHost";

const ORG_QUERY_PARAM = "org";

/**
 * Normalize org from path/query. Enforces SSO subdomain rules (reserved + `[a-z0-9-]+`).
 */
export function normalizeOrg(org: string | null | undefined): string | null {
  return normalizeSsoOrgLabel(org);
}

function toSearchParams(search: string | URLSearchParams): URLSearchParams {
  if (search instanceof URLSearchParams) {
    return search;
  }

  const queryString = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(queryString);
}

/**
 * Extract org from route params (path-based: `/login/{orgKey}/`).
 * Returns null for reserved/invalid segments.
 */
export function getOrgFromParams(
  params: Record<string, string | undefined>
): string | null {
  return normalizeOrg(params.orgKey ?? params.org ?? null);
}

/**
 * Legacy `?org=` support (redirect to path or subdomain depending on flag).
 */
export function getOrgFromSearchParams(
  search: string | URLSearchParams
): string | null {
  const org = toSearchParams(search).get(ORG_QUERY_PARAM);
  return org === null ? null : normalizeOrg(org);
}

/**
 * Resolve login org: hostname when subdomain cutover flag is on, else path params.
 */
export function resolveLoginOrg(input: {
  params: Record<string, string | undefined>;
  hostname: string;
}): string | null {
  if (isOrgSubdomainHostEnabled()) {
    return resolveOrgKeyFromHostname(input.hostname);
  }
  return getOrgFromParams(input.params);
}

/**
 * Build a path under `/login/{org}/...` when org present (flag off),
 * or host-scoped `/login/...` when subdomain flag is on.
 */
export function buildOrgLoginPath(
  org: string | null | undefined,
  subPath?: string,
  queryParams?: Record<string, string>
): string {
  const normalizedOrg = normalizeOrg(org);
  const hostMode = isOrgSubdomainHostEnabled();

  const base =
    !hostMode && normalizedOrg ? `/login/${normalizedOrg}` : "/login";
  const path = subPath ? `${base}/${subPath}` : `${base}/`;

  if (!queryParams || Object.keys(queryParams).length === 0) {
    if (hostMode) {
      return subPath ? path : `${base}/`;
    }
    return normalizedOrg ? path : base;
  }

  const params = new URLSearchParams(queryParams);
  return `${path}?${params.toString()}`;
}

export function withOrgQuery(
  path: string,
  org: string | null | undefined
): string {
  const normalizedOrg = normalizeOrg(org);
  const hashIndex = path.indexOf("#");
  const pathAndQuery = hashIndex === -1 ? path : path.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);

  if (normalizedOrg === null) {
    return path;
  }

  const queryIndex = pathAndQuery.indexOf("?");
  const pathname =
    queryIndex === -1 ? pathAndQuery : pathAndQuery.slice(0, queryIndex);
  const queryString =
    queryIndex === -1 ? "" : pathAndQuery.slice(queryIndex + 1);
  const params = new URLSearchParams(queryString);

  params.set(ORG_QUERY_PARAM, normalizedOrg);

  const nextQuery = params.toString();
  const nextPath = nextQuery ? `${pathname}?${nextQuery}` : pathname;

  return `${nextPath}${hash}`;
}

/**
 * Entra unauthorized failure path.
 * Flag off: `/login/{org}/?entra_error=unauthorized`
 * Flag on: `/login/?entra_error=unauthorized` (org is on hostname)
 */
export function buildEntraUnauthorizedLoginPath(
  org: string | null | undefined
): string {
  if (isOrgSubdomainHostEnabled()) {
    return "/login/?entra_error=unauthorized";
  }

  const normalizedOrg = normalizeOrg(org);

  if (normalizedOrg !== null) {
    return `/login/${normalizedOrg}/?entra_error=unauthorized`;
  }

  return "/login?entra_error=unauthorized";
}
